import { Building2, Landmark, Car, Wrench, Paintbrush, Zap, Building, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";

const services = [
  {
    icon: Building2,
    title: "B2B Solutions",
    subtitle: "Corporate Mobility Partner",
    description: "Enterprise-level mobility solutions tailored to your business needs.",
    color: "green",
  },
  {
    icon: Landmark,
    title: "B2G Services",
    subtitle: "Government & Public Sector",
    description: "Supply & operate sustainable mobility for governmental institutions.",
    color: "blue",
  },
  {
    icon: Car,
    title: "Leasing Programs",
    subtitle: "On-Demand Fleet Leasing",
    description: "Flexible leasing options for events, seasonal operations, and temporary deployments.",
    color: "teal",
  },
  {
    icon: Wrench,
    title: "Service & Maintenance",
    subtitle: "Authorized Service Provider",
    description: "Comprehensive after-sales support, repairs, and technical maintenance.",
    color: "purple",
  },
  {
    icon: Paintbrush,
    title: "Fleet Customization",
    subtitle: "Modification & Branding",
    description: "Custom branding, livery, and equipment integration for your fleet.",
    color: "gold",
  },
  {
    icon: Zap,
    title: "Motorized Mobility",
    subtitle: "Electric & Combustion",
    description: "Complete range of motorized vehicles for diverse operational needs.",
    color: "rose",
  },
  {
    icon: Building,
    title: "Mega Projects",
    subtitle: "Run & Operate",
    description: "End-to-end management of large-scale mobility deployments.",
    color: "cyan",
  },
];

const colorMap: Record<string, { bg: string; iconBg: string; border: string; text: string; glow: string }> = {
  green: {
    bg: "from-eco-green/15 to-eco-green/5",
    iconBg: "from-eco-green to-eco-green-light",
    border: "border-eco-green/20 hover:border-eco-green/40",
    text: "text-eco-green",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--eco-green)/0.3)]",
  },
  blue: {
    bg: "from-sky-blue/15 to-sky-blue/5",
    iconBg: "from-sky-blue to-sky-blue-light",
    border: "border-sky-blue/20 hover:border-sky-blue/40",
    text: "text-sky-blue",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--sky-blue)/0.3)]",
  },
  teal: {
    bg: "from-teal-bright/15 to-teal-bright/5",
    iconBg: "from-teal-bright to-eco-green",
    border: "border-teal-bright/20 hover:border-teal-bright/40",
    text: "text-teal-bright",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--teal-bright)/0.3)]",
  },
  purple: {
    bg: "from-purple/15 to-purple/5",
    iconBg: "from-purple to-rose",
    border: "border-purple/20 hover:border-purple/40",
    text: "text-purple",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--purple)/0.3)]",
  },
  gold: {
    bg: "from-primary/15 to-amber-glow/5",
    iconBg: "from-primary to-amber-glow",
    border: "border-primary/20 hover:border-primary/40",
    text: "text-primary",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--primary)/0.3)]",
  },
  rose: {
    bg: "from-rose/15 to-rose/5",
    iconBg: "from-rose to-purple",
    border: "border-rose/20 hover:border-rose/40",
    text: "text-rose",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--rose)/0.3)]",
  },
  cyan: {
    bg: "from-sky-blue/15 to-teal-bright/5",
    iconBg: "from-sky-blue to-teal-bright",
    border: "border-sky-blue/20 hover:border-teal-bright/40",
    text: "text-sky-blue",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--sky-blue)/0.3)]",
  },
};

const Services = () => {
  return (
    <section id="services" className="py-24 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-card via-card/80 to-card" />
      <div className="absolute -left-40 top-1/4 w-96 h-96 rounded-full bg-eco-green/10 blur-3xl animate-pulse" />
      <div className="absolute -right-40 bottom-1/4 w-96 h-96 rounded-full bg-sky-blue/10 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl" />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${i % 3 === 0 ? 'bg-eco-green/30' : i % 3 === 1 ? 'bg-sky-blue/30' : 'bg-primary/30'}`}
            style={{
              left: `${5 + i * 10}%`,
              top: `${15 + (i % 4) * 20}%`,
              animation: `float ${3 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <VibrantSectionHeader
          badge="Specialist At"
          title="Our Services"
          description="Comprehensive sustainable mobility solutions aligned with Vision 2030"
          badgeColor="green"
        />

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const colors = colorMap[service.color];
            const isLastItem = index === services.length - 1 && services.length % 3 === 1;
            return (
              <div
                key={service.title}
                className={`group p-8 rounded-2xl bg-gradient-to-br ${colors.bg} border ${colors.border} backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 ${colors.glow} relative overflow-hidden ${isLastItem ? 'lg:col-start-2' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Glow effect */}
                <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br ${colors.iconBg} blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.iconBg} flex items-center justify-center mb-6 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-1 text-foreground">{service.title}</h3>
                  <p className={`${colors.text} font-semibold text-sm mb-3`}>{service.subtitle}</p>
                  <p className="text-muted-foreground text-sm">{service.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button 
            size="xl" 
            asChild
            className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300 hover:scale-105"
          >
            <Link to="/services">
              View All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
